import type { ProjectStatus } from "./types";

export const MAX_PERSON_BYTES = 15 * 1024 * 1024;
export const MAX_PROJECT_BYTES = 500 * 1024 * 1024;
export const ALLOWED_EXTENSIONS = new Set(["mp3"]);

export function parseAuditionFilename(filename: string) {
  const dot = filename.lastIndexOf(".");
  if (dot <= 0) return { valid: false, roleName: "", cvAbbr: "", error: "文件缺少扩展名" };
  const extension = filename.slice(dot + 1).toLowerCase();
  if (!ALLOWED_EXTENSIONS.has(extension)) {
    return { valid: false, roleName: "", cvAbbr: "", error: "只允许上传 MP3 文件" };
  }
  const base = filename.slice(0, dot).normalize("NFC").trim();
  const lastSpace = base.search(/\s+(?=\S+$)/);
  const separatorAt = Math.max(base.lastIndexOf("_"), base.lastIndexOf("-"), base.lastIndexOf("+"), lastSpace);
  if (separatorAt < 1 || separatorAt === base.length - 1) {
    return { valid: false, roleName: "", cvAbbr: "", error: "文件名应为：角色名 + 分隔符 + CV名字或缩写" };
  }
  const roleName = base.slice(0, separatorAt).trim();
  const cvAbbr = base.slice(separatorAt + 1).trim();
  if (!roleName || !cvAbbr) {
    return { valid: false, roleName, cvAbbr, error: "角色名和CV名字或缩写不能为空" };
  }
  return { valid: true, roleName, cvAbbr, extension };
}

export function normalizePhone(value: string) {
  return value.replace(/\D/g, "");
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function getProjectStatus(deadline: string, cloudDeleted = false): ProjectStatus {
  if (cloudDeleted) return "cloud_deleted";
  const end = new Date(deadline).getTime();
  const now = Date.now();
  if (now <= end) return "accepting";
  if (now > end + 30 * 86400000) return "retention_pending";
  return "closed";
}

export function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }).format(new Date(value));
}

export function countdown(deadline: string) {
  const diff = new Date(deadline).getTime() - Date.now();
  if (diff <= 0) return "已截止";
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  return days > 0 ? `剩余 ${days} 天 ${hours} 小时` : `剩余 ${hours} 小时`;
}

export async function sha256(file: File | Blob) {
  const buffer = await file.arrayBuffer();
  if (globalThis.crypto?.subtle) {
    const digest = await globalThis.crypto.subtle.digest("SHA-256", buffer);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  // Web Crypto is unavailable on plain HTTP in Chrome. Keep hashing local so
  // the upload page remains functional until the custom domain has HTTPS.
  const bytes = new Uint8Array(buffer);
  const bitLength = bytes.length * 8;
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(bytes); padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000), false);
  view.setUint32(paddedLength - 4, bitLength >>> 0, false);
  const k = new Uint32Array([
    0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
    0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
    0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
    0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
    0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
    0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
    0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
    0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
  ]);
  const rotr = (value: number, shift: number) => (value >>> shift) | (value << (32 - shift));
  const h = new Uint32Array([0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19]);
  const w = new Uint32Array(64);
  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4, false);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a,b,c,d,e,f,g,hh] = h;
    for (let i = 0; i < 64; i++) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + s1 + ch + k[i] + w[i]) >>> 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (s0 + maj) >>> 0;
      hh=g; g=f; f=e; e=(d+t1)>>>0; d=c; c=b; b=a; a=(t1+t2)>>>0;
    }
    h[0]=(h[0]+a)>>>0; h[1]=(h[1]+b)>>>0; h[2]=(h[2]+c)>>>0; h[3]=(h[3]+d)>>>0;
    h[4]=(h[4]+e)>>>0; h[5]=(h[5]+f)>>>0; h[6]=(h[6]+g)>>>0; h[7]=(h[7]+hh)>>>0;
  }
  return [...h].map((value) => value.toString(16).padStart(8, "0")).join("");
}

export function safeWindowsName(value: string) {
  return value.replace(/[<>:"/\\|?*\x00-\x1f]/g, "_").replace(/[. ]+$/g, "").slice(0, 80) || "未命名";
}
